import { Fragment, Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import styles from './Dashboard.module.scss';
import TabSelector from '../../common/TabSelector/TabSelector';
import { fetchAicaGradeData, fetchGraphData, fetchHeadlineData, intializeChartsAction } from '../../store/dashboard/actions';
import DataSource from './components/DataSource/DataSource';
import StatCard from './components/StatCard/StatCard';
import Graph from './components/Graph/Graph';
import Overview from './DashboardModules/Overview/Overview';
import ShareholdingPattern from './DashboardModules/Overview/components/ShareholdingPattern/ShareholdingPattern';
import FundingHistory from './DashboardModules/Overview/components/FundingHistory/FundingHistory';
import {
    fetchSingleCompanyDetails,
    setShowInviteUserModal,
    setShowRefreshReport,
    setShowUnlockAicaAndAddToLeads,
    setShowUnlockAicaConfirmation,
    setShowUpgradeToScan,
    setMobileOverviewSource,
} from '../../store/investor/action';
import { USER_TYPE } from '../../utils/constants/user';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { INVESTOR_ROUTES, COMPANY_ROUTES } from '../../utils/constants/routesConst';
import { initializeDatepickerDates } from '../../utils/dateUtils';
import { FEATURES } from '../../store/user/constants';
import CreditBureau from './DashboardModules/CreditBureau/CreditBureau';
import { Mixpanel } from '../../utils/mixpanel';
import {
    AICA_GST_MODULE_CLICKED,
    INVESTOR_MOBILE_COMPANY_VIEW,
    INVESTOR_MOBILE_BACK_TO_LIST,
} from '../../utils/constants/mixpanelEvents/investorEvents';
import { useDashboardTabAnalytics } from './useDashboardTabAnalytics';
import BankDataTable from './DashboardModules/BankDataTable/BankDataTable';
import Financials from './DashboardModules/Financials/Financials';
import { DASHBOARD_DATA_TYPES, dataTypeSelectorOptions } from './dashboardConstants';
import UnlockScanModal from './components/UnlockScanModal/UnlockScanModal';
import { ACCESS_TIERS, ACCESS_TYPES, UnlockAICASourcePage } from '../../store/investor/constants';
import UnlockScanButton from './DashboardModules/Overview/components/Buttons/UnlockScan/UnlockScanButton';
import { useMcaPolling } from '../../utils/customHooks';
import AddToScanButton from './DashboardModules/Overview/components/Buttons/AddToScan/AddToScanButton';
import InviteCompanyModal, { INVITE_SCREENS } from './components/InviteCompanyModal/InviteCompanyModal';
import OperatingInflowReconciliation from './DashboardModules/Reconciliation/OperatingInflowReconciliation/OperatingInflowReconciliation';
import { mixpanelDashboardTabUpdates, mixpanelRegisterTabChangeClick } from '../../store/dashboard/dashboard_mixpanel';
import ManageDealDrawer from '../../common/ManageDealDrawer/ManageDealDrawer';
import { PendingDataIncomplete, needInfoSelected } from '../../assets/hostedassets';
import RelatedParties from './DashboardModules/RelatedParties/RelatedParties';
import ManagementProfileTab from './DashboardModules/ManagementProfileTab/ManagementProfileTab';
import ConfirmationPopup from '../../common/ConfirmationPopup/ConfirmationPopup';
import PortfolioSuccess from '../Investor/Portfolio/components/PortfolioSuccess/PortfolioSuccess';
import FinancialMetrics from './DashboardModules/Overview/components/FinancialMetrics/FinancialMetrics';
import { moveTierAndUnlockAICAReport } from '../../store/investor/apiHelpers';
import AicaOverview from '../../common/AicaOverview/AicaOverview';
import GstOverview from './DashboardModules/GstOverview';
import GstAnalysisPanel from './DashboardModules/GstOverview/GstAnalysisPanel';
import LegalCases from './components/DashboardTables/LegalCases/LegalCases';
import clsx from 'clsx';
import DebtDetails from './DashboardModules/DebtDetails/DebtDetails';
import DirectorsBusinessLoans from './DashboardModules/DebtDetails/DirectorsBusinessLoans';
import useLegacyBusinessDataFetch from '../DataRoom/useLegacyBusinessDataFetch';
import A4Page from '../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../store/pdfReport/pdfReport_constants';
import { getDebtProductsData } from '../../store/debtProducts/apiHelper_debtProducts';
import { closeCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import ChargeDetails from './DashboardModules/Overview/components/ChargeDetails/ChargeDetails';
import { SOURCE } from '../../common/NeedInfoV2/NeedInfoV2Constants';
import { getAllTaskTrackerData } from '../../store/investor/investorApiHelper';
import dayjs from 'dayjs';
import AskAicaV2 from './DashboardModules/Overview/components/AskAicaV2/AskAicaV2';
import { TabModule } from '../../utils/constants/commonConstants';
import McaRefreshLoader from '../../common/McaRefreshLoader/McaRefreshLoader';
import { dismissBanner, fetchBankingPendingStatus, resetBankingState } from '../../store/banking/bankingSlice';
import { useExtractBankStatements } from '../../store/banking/useExtractBankStatements';
import {
    selectBankingBannerDismissedForOrg,
    selectBankingExtractedCount,
    selectBankingInFlightCount,
    selectBankingPendingCount,
    selectBankingShowTab,
    selectBankingTriggering,
} from '../../store/banking/selectors';
import PendingBankStatementsBanner from '../../common/PendingBankStatementsBanner/PendingBankStatementsBanner';
import { BANNER_MODE, BannerMode, CLICK_SOURCE } from '../../store/banking/constants';
import { useVisibilityConfig } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { useIsMobile } from '../../common/_custom/Hooks/useIsMobile';
import DashboardHeader from './components/DashboardHeader/DashboardHeader';
import { getMobileCompanySections, MobileCompanySection } from './mobile/mobileCompanySections';
import MobileAskAicaOrb from './mobile/MobileAskAicaOrb';
import { MobileAskAicaCompany } from './mobile/MobileAskAica/types';

// Lazy on purpose: the chat surface pulls the shared MessagesContainer chain
// (react-markdown and friends) - loading it eagerly here would put that chain
// into every module graph that imports the store (Dashboard <- AicaPDFReport <-
// pdfReport_reducer <- store.ts), breaking unrelated Jest suites and bloating
// the main bundle. It loads on the first orb tap instead.
const MobileAskAicaScreen = lazy(() => import('./mobile/MobileAskAica/MobileAskAicaScreen'));

/**
 * @Description Dashboard landing component
 */

export { DASHBOARD_DATA_TYPES, dataTypeSelectorOptions };

// The global Deals listing (`/investor/app/deals`) — mirrors GLOBAL_DEALS_PATH in
// notificationNavigation.ts, composed from the same existing route segments.
const INVESTOR_DEALS_PATH = `${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DEALS}`;

// Maps the mobile-overview return-path stored in Redux (investor.mobileOverviewSource)
// to a human-readable Mixpanel source label.
const getMobileOverviewSource = (fromPath: string | null) => {
    if (fromPath === INVESTOR_DEALS_PATH || fromPath === COMPANY_ROUTES.DEALS) return 'deals';
    if (fromPath === INVESTOR_ROUTES.APP_SCAN) return 'leads';
    return 'direct';
};

function Dashboard({ pdfReportSection = '', isPDFReport = false }) {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [latestFetchDate, setLatestFetchDate] = useState<string | null>(null);
    const selectedTabIdQuery = searchParams.get('selectedTab');
    const showPortfolioSuccess = searchParams.get('portfolioSuccess') === 'true';
    const showFinancialMetrics = searchParams.get('showFinancialMetrics');
    const dataTypeQuery = searchParams.get('dataType');
    const refreshReportQueryParam = searchParams.get('showRefreshReport');
    const investorData = useAppSelector((state) => state.investor);
    const dashboard = useAppSelector((state) => state.dashboard);
    const periodSelectorData = useAppSelector((state) => state.periodSelector);

    const showDealDrawer = investorData.dealDrawer.open;
    const source = investorData.dealDrawer.drawerSource;
    const unlockAicaReportDetails = investorData.unlockAicaReport;
    const unlockAicaAndAddToLeadsConfirmation = investorData.unlockAicaReportAndAddToLeads;
    const permissions = investorData.permissions;
    const { mcaDocData } = useMcaPolling(params.id || '');

    const bankingPendingCount = useAppSelector(selectBankingPendingCount);
    const bankingInFlightCount = useAppSelector(selectBankingInFlightCount);
    const bankingExtractedCount = useAppSelector(selectBankingExtractedCount);
    const bankingBannerDismissed = useAppSelector((state) => selectBankingBannerDismissedForOrg(state, params.id));
    const bankingTriggering = useAppSelector(selectBankingTriggering);

    const hasInFlightBanking = bankingTriggering || bankingInFlightCount > 0;
    // In-flight wins (loader, no CTA → blocks double-trigger). Else pending → CTA. Else hide.
    const bankingBannerMode: BannerMode = hasInFlightBanking ? BANNER_MODE.IN_PROGRESS : BANNER_MODE.PENDING;
    // Role-gate Extract Now CTA via polus visibilityConfig. WRITE = can click; READ/HIDDEN = no CTA.
    const { canWrite: canExtractNow } = useVisibilityConfig();
    const bankingExtractAllowed = canExtractNow(VISIBILITY_KEYS.BANKING.EXTRACT_NOW);

    // "Triggered" set excludes LOCKED — represents what's actually in / through the pipeline.
    const bankingTotalCount = useMemo(() => bankingInFlightCount + bankingExtractedCount, [bankingInFlightCount, bankingExtractedCount]);
    const bankingProcessedCount = bankingExtractedCount;
    const extractBankStatements = useExtractBankStatements(params.id);

    const dashboardDataNew = dashboard.dashboardReducer;
    const aicaGradeData = dashboardDataNew.aicaGradeData;
    const metricScoreGenerated = aicaGradeData.metricScoreGenerated ?? false;

    const [dashboardTab, setDashboardTab] = useState({
        selectedTab: '',
        tabType: '',
    });
    const {
        goBackToPortfolio,
        selectedCompany,
        setSelectedCompany,
        exportData,
        user,
        isUserRestricted,
        refreshReportStatus,
        downloadPDFStatus,
        refetchOverviewRef,
    } = useLegacyBusinessDataFetch({
        fetchAicaGrade: true,
    });
    const [dataType, setDataType] = useState(DASHBOARD_DATA_TYPES.CHARTS);
    const [showInviteCompanyModal, setShowInviteCompanyModal] = useState(false);
    const [showPDFReportSection, setShowPDFReportSection] = useState(true);
    const [triggerMain, setTriggerMain] = useState(false);
    const [mobileAskAicaOpen, setMobileAskAicaOpen] = useState(false);

    const header = document.getElementById('header') ?? document.createElement('div');

    const isScanLockedForUser = permissions?.[ACCESS_TIERS.SCAN] === ACCESS_TYPES.LOCKED;
    const isDemoProfile = !!selectedCompany?.demoOrg;

    const dashboardConfig: any = investorData.config;
    const headlinePanelData = dashboardDataNew?.headlineData?.data;
    const dataSource = dashboardDataNew?.dataSource || {};

    const selectedTabConfig = dashboardConfig?.tabConfigs?.[dashboardTab.selectedTab];

    const isBankingBannerVisible =
        dashboardTab.selectedTab === TabModule.BANKING_AND_FINANCIALS &&
        bankingExtractAllowed &&
        ((bankingBannerMode === BANNER_MODE.IN_PROGRESS && hasInFlightBanking && bankingTotalCount > 0) ||
            (bankingBannerMode === BANNER_MODE.PENDING && bankingPendingCount > 0 && !bankingBannerDismissed));

    const chartsToShow = selectedTabConfig?.chartPanel?.cards || [];
    const chartsData = dashboardDataNew?.charts || {};
    const showAlerts = !!user.domainMetadata?.availableFeatures?.includes(FEATURES.ALERTS);
    const showUpgradeToScanModal = investorData.showUpgradeToScan;
    const showRefreshReport = investorData.showRefreshReport;
    const showDataGapCTA = permissions.showDataGap;

    const HEADLINE_PANEL_CONSTANTS = {
        OPEN_LEGAL_CASES: 'Open Legal Cases',
        COMPLIANCES: 'compliances',
    };

    const generateSeparateOverview = [
        'overview',
        'creditBureau',
        'auditedFinancials',
        'relatedParty',
        'debtDetails',
        'managementProfile',
        TabModule.GST,
    ];

    const headlinePanelDataPollingRef: any = useRef();

    const initialiseHeadlinePanelPolling = () => {
        if (!headlinePanelDataPollingRef.current) {
            fetchHeadlinePanelData(false);
            headlinePanelDataPollingRef.current = setInterval(() => {
                fetchHeadlinePanelData(true);
            }, 10000);
        }
    };
    // const pendingCount = useAppSelector((state) => state.investor.pendingDataCount);
    //
    // const getPendingDocumentsCount = async () => {
    //     await getDataGapDocs(
    //         {
    //             assesseeOrgId: params.id ?? '',
    //         },
    //         {
    //             onSuccess: (res) => {
    //                 dispatch(updateNeedInfoFileNumber(res.pendingCount));
    //             },
    //         },
    //     );
    // };

    useEffect(() => {
        if (pdfReportSection) {
            setDashboardTab({
                tabType: 'dynamic',
                selectedTab: pdfReportSection,
            });
        }
    }, [pdfReportSection]);

    // Note: fetchOwners is now called in InvestorPrivateRoutes to ensure it's available globally for comment mentions

    const getDebtProductsDataToSaveInCache = async () => {
        const payload = {
            orgId: params.id as string,
            aggregatorId: user.userData.investorOrgId,
        };
        await getDebtProductsData(payload);
    };

    useEffect(() => {
        // getPendingDocumentsCount();
        getDebtProductsDataToSaveInCache();

        return () => {
            dispatch(closeCommentsThread());
        };
    }, []);

    useEffect(() => {
        if (!showDealDrawer) {
            setTriggerMain(true);
        } else {
            setTriggerMain(false);
        }
    }, [showDealDrawer]);

    const closeHeadlinePanelPolling = () => {
        clearInterval(headlinePanelDataPollingRef.current);
        headlinePanelDataPollingRef.current = null;
    };

    useEffect(() => {
        if (dashboardTab.selectedTab === 'bankingAndFinancials') {
            setDataType(DASHBOARD_DATA_TYPES.TABLE);
            return;
        }
        if (dashboardTab.selectedTab === 'auditedFinancials' && searchParams.get('financialsType') === 'cashflowFromBanking') {
            setDataType(DASHBOARD_DATA_TYPES.TABLE);
            return;
        }
        if (dataTypeQuery) {
            // Banking tab should always be in table mode, ignore URL parameter for chart/table toggle
            setDataType(dataTypeQuery);
        }
    }, [dataTypeQuery, dashboardTab.selectedTab, searchParams]);

    useDashboardTabAnalytics({
        selectedTab: dashboardTab.selectedTab,
        investorOrgId: user.userData.investorOrgId,
        companyOrgId: params.id,
    });

    useEffect(() => {
        if (dashboardConfig?.selectedTabId && headlinePanelDataPollingRef.current) {
            closeHeadlinePanelPolling();
        }

        return () => {
            closeHeadlinePanelPolling();
        };
    }, [dashboardTab.selectedTab]);

    useEffect(() => {
        if (params.id) {
            Mixpanel.register({
                AssesseeOrgId: params.id,
            });
        }
    }, [params.id]);

    useEffect(() => {
        if (refreshReportQueryParam) {
            deleteFromSearchParams('showRefreshReport');
            dispatch(setShowRefreshReport(true));
        }
    }, [refreshReportQueryParam]);

    useEffect(() => {
        if (dashboardConfig?.tabs?.length && (selectedTabIdQuery || dashboardConfig?.selectedTabId)) {
            const selectedTabFromApi =
                dashboardConfig?.selectedTabId && dashboardConfig?.tabs?.find((tab) => tab.id === dashboardConfig?.selectedTabId);
            const selectedTabFromQuery = selectedTabIdQuery && dashboardConfig?.tabs?.find((tab) => tab.id === selectedTabIdQuery);
            const isGstQueryFallback = selectedTabIdQuery === TabModule.GST;
            if (!pdfReportSection && !dashboardTab.selectedTab) {
                if (selectedTabFromQuery) {
                    setDashboardTab({ selectedTab: selectedTabFromQuery.id, tabType: selectedTabFromQuery.type });
                } else if (isGstQueryFallback) {
                    setDashboardTab({ selectedTab: selectedTabIdQuery, tabType: 'dynamic' });
                } else if (selectedTabFromApi) {
                    setDashboardTab({ selectedTab: selectedTabFromApi.id, tabType: selectedTabFromApi.type });
                }
            }
        }
    }, [dashboardConfig?.selectedTabId, dashboardConfig?.tabs, selectedTabIdQuery]);
    useEffect(() => {
        if (!dashboardTab.selectedTab || !params.id) return;
        appendSearchParams({ selectedTab: dashboardTab.selectedTab });
        // GST tab + the two legacy parents own their own fetches via
        // GstAnalysisPanel — skip here to avoid clobbering dataSource.
        const ownedByGstOverview =
            dashboardTab.selectedTab === TabModule.GST ||
            dashboardTab.selectedTab === TabModule.REVENUE ||
            dashboardTab.selectedTab === TabModule.VENDOR_EXPENSES;
        if (ownedByGstOverview) return;
        dispatch(intializeChartsAction({ charts: chartsToShow }));
        fetchHeadlinePanelData();
        //eslint-disable-next-line
    }, [dashboardTab.selectedTab, params.id, dataType]);

    useEffect(() => {
        if (dataType && dashboardTab.selectedTab) {
            mixpanelDashboardTabUpdates(dataType, dashboardTab, selectedCompany, user);
        }
    }, [dataType, dashboardTab.selectedTab]);

    useEffect(() => {
        //* If charts data is absent for the section, then PDF report section will be removed
        if (chartsToShow?.length && pdfReportSection && Object.keys(chartsData)?.length) {
            let allDataUnavailable = true;
            for (const chartName of chartsToShow) {
                if (chartsData[chartName]?.isFetched && chartsData[chartName]?.plots?.length) {
                    allDataUnavailable = false;
                    break;
                }
            }
            setShowPDFReportSection(!allDataUnavailable);
        }
    }, [chartsData, pdfReportSection, chartsToShow]);

    useEffect(() => {
        fetchLatestUpdated();
    }, [params.id]);

    useEffect(() => {
        if (params.id) {
            dispatch(resetBankingState());
            dispatch(fetchBankingPendingStatus({ orgId: params.id }));
        }
    }, [params.id]);

    useEffect(() => {
        if (params.id && dashboardTab.selectedTab === TabModule.BANKING_AND_FINANCIALS) {
            dispatch(fetchBankingPendingStatus({ orgId: params.id }));
        }
    }, [dashboardTab.selectedTab]);

    const isMagicUploadOpen = searchParams.get('magicUpload') === 'true';
    const wasMagicUploadOpen = useRef(false);
    useEffect(() => {
        if (wasMagicUploadOpen.current && !isMagicUploadOpen && params.id) {
            dispatch(fetchBankingPendingStatus({ orgId: params.id }));
        }
        wasMagicUploadOpen.current = isMagicUploadOpen;
    }, [isMagicUploadOpen]);

    // RECUR-25620: banking tab is always visible when present in dashboardConfig.
    // Empty / locked / loaded states are rendered inside BankDataTable + sidebar dot on TabSelector.
    // Apply staging's showInSidebar filter (orthogonal to banking visibility).
    const visibleTabs = useMemo(
        () => dashboardConfig?.tabs?.filter((tab: any) => tab.showInSidebar !== false) ?? [],
        [dashboardConfig?.tabs],
    );

    // Same visibleTabs desktop's sidebar (TabSelector) renders from — the
    // mobile section list is derived from it too, so a tab hidden from
    // desktop's config is hidden on mobile without a second hardcoded list.
    const mobileCompanySections = useMemo(() => getMobileCompanySections(visibleTabs), [visibleTabs]);

    const appendSearchParams = (paramsToAppend) => {
        const existingParams = Object.fromEntries(searchParams.entries());
        const targetTab = paramsToAppend?.selectedTab ?? dashboardTab.selectedTab;
        if (targetTab !== TabModule.CREDIT_BUREAU) {
            ['bureauType', 'directorBureauType', 'directorId', 'focusDirectorId', 'report'].forEach((key) => delete existingParams[key]);
        }
        const updatedParams = {
            ...existingParams,
            ...paramsToAppend,
        };
        setSearchParams(updatedParams, { state: location.state });
    };

    // Navigate to a specific tab (updates both URL and state)
    const navigateToTab = (tabId: string, additionalParams?: Record<string, string>) => {
        const tab = dashboardConfig?.tabs?.find((t) => t.id === tabId);
        if (tab) {
            setDashboardTab({ selectedTab: tab.id, tabType: tab.type });
            appendSearchParams({ selectedTab: tabId, ...additionalParams });
        }
    };

    const deleteFromSearchParams = (paramKeyToRemove: string) => {
        let existingParams = Object.fromEntries(searchParams.entries());
        delete existingParams[paramKeyToRemove];
        setSearchParams(existingParams, { state: location.state });
    };

    // Legacy ?selectedTab=revenue|vendorExpenses URL → GST sub-tab redirect.
    // Replace (not push) so back-button doesn't bounce.
    useEffect(() => {
        if (!dashboardConfig?.tabs?.length) return;
        if (selectedTabIdQuery !== TabModule.REVENUE && selectedTabIdQuery !== TabModule.VENDOR_EXPENSES) return;
        const subTab = selectedTabIdQuery === TabModule.REVENUE ? 'revenueAnalysis' : 'expensesAnalysis';
        const existing = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...existing, selectedTab: TabModule.GST, subTab }, { replace: true, state: location.state });
        const gstTab = dashboardConfig.tabs.find((t: any) => t.id === TabModule.GST);
        if (gstTab) setDashboardTab({ selectedTab: gstTab.id, tabType: gstTab.type });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTabIdQuery, dashboardConfig?.tabs?.length]);

    const getGridRowHeight = (chartName, index) => {
        const pos = Math.floor(index / 2);
        const data = chartsData[chartName];
        const tileSize = data?.metadata?.tileSize || 0;
        if (tileSize > 1) return `${pos + 1} / ${pos + 1 + tileSize}`;
    };

    const getStaticComponent = () => {
        let TabComponent;

        switch (dashboardTab.selectedTab) {
            case 'overview':
                TabComponent = (
                    <Overview
                        selectedCompany={selectedCompany}
                        refetchOverviewRef={refetchOverviewRef}
                        appendSearchParams={appendSearchParams}
                        navigateToTab={navigateToTab}
                        triggerMain={triggerMain}
                    />
                );
                break;
            case 'relatedParty': {
                const isSoleProp = selectedCompany?.isSoleProp || false;
                TabComponent = (
                    <div className={styles.RelatedPartyContainer}>
                        <RelatedParties mcaDocData={mcaDocData} />
                        {!isSoleProp && <ShareholdingPattern tabName="relatedParty" />}
                        {!isSoleProp && <FundingHistory />}
                    </div>
                );
                break;
            }
            case 'managementProfile':
                TabComponent = <ManagementProfileTab navigateToTab={navigateToTab} mcaDocData={mcaDocData} />;
                break;
            case 'creditBureau':
                TabComponent = (
                    <CreditBureau
                        selectedTabConfig={selectedTabConfig}
                        getGridRowHeight={getGridRowHeight}
                        selectedCompany={selectedCompany}
                    />
                );
                break;
            case 'auditedFinancials':
                TabComponent = <Financials companyName={selectedCompany?.orgName || ''} />;
                break;
            case 'reconciliation':
                TabComponent = <OperatingInflowReconciliation assesseeOrgId={params.id} />;
                break;
            case 'legalChecks':
                TabComponent = (
                    <LegalCases
                        assesseeOrgId={params.id ?? ''}
                        companyName={selectedCompany?.orgName || ''}
                        initialiseHeadlinePanelPolling={initialiseHeadlinePanelPolling}
                    />
                );
                break;
            case 'debtDetails':
                TabComponent = (
                    <div className={styles.DebtDetailsContainer}>
                        <div className={styles.DebtDetailsHeader}>
                            <span>Debt Details</span>
                        </div>
                        <div className={styles.DebtDetailsBody}>
                            <DebtDetails />
                            <ChargeDetails isPDFReport={!!pdfReportSection} mcaDocApiData={mcaDocData} />
                            <DirectorsBusinessLoans />
                        </div>
                    </div>
                );
                break;
            default:
                TabComponent = <></>;
        }

        return (
            <>
                {!generateSeparateOverview.includes(dashboardTab.selectedTab) && (
                    <>
                        <div className={styles.ModuleTitle}>
                            {dashboardConfig?.tabs.find((tab) => tab.id === dashboardTab.selectedTab)?.label}
                        </div>
                        <AicaOverview style={{ marginBottom: '1rem' }} module={dashboardTab.selectedTab} />
                    </>
                )}
                {TabComponent}
            </>
        );
    };

    const fetchHeadlinePanelData = (isPolling = false) => {
        const [fromDate, previousMonthLastDate] = initializeDatepickerDates(periodSelectorData.selectedPeriod.name);
        dispatch(
            fetchHeadlineData(
                {
                    tabName: dashboardTab.selectedTab,
                    startDate: fromDate,
                    endDate: previousMonthLastDate,
                    tenure: periodSelectorData.selectedPeriod.name || '',
                    reportId: selectedCompany?.reportId || '',
                },
                { isPolling: isPolling },
            ),
        );
    };

    const fetchLatestUpdated = () => {
        getAllTaskTrackerData(
            {
                orgId: params.id as string,
            },
            {
                onSuccess: (response) => {
                    const latestUpdated = response?.['AION_GST_TRANSACTIONS_FETCH_INGEST']?.dateCreated;
                    setLatestFetchDate(dayjs(latestUpdated).format('DD MMM YYYY'));
                },
            },
        );
    };
    const fetchGraph = (graphName, isPollingCall = false) => {
        const [fromDate, toDate] = initializeDatepickerDates(periodSelectorData.selectedPeriod.name);
        const graphNameAry = [graphName];
        dispatch(
            fetchGraphData(
                {
                    graphNames: graphNameAry,
                    tabName: dashboardTab.selectedTab,
                    startDate: fromDate,
                    endDate: toDate,
                    tenure: periodSelectorData.selectedPeriod.name,
                    reportId: selectedCompany?.reportId || '',
                    // graphType: props.graphData.type,
                },
                {
                    isPollingCall: isPollingCall,
                },
            ),
        );
    };

    const getTableData = () => {
        switch (dashboardTab.selectedTab) {
            case 'bankingAndFinancials':
                return <BankDataTable setDashboardTab={setDashboardTab} />;
        }
        // revenue / vendorExpenses tables live inside the GST module
        // (DashboardModules/GstOverview/GstAnalysisPanel) — Dashboard no longer
        // renders them at the top level.
    };

    const onUnlockReportSuccess = (callbackFunction = () => {}) => {
        refetchOverviewRef.current = true;
        dispatch(
            fetchSingleCompanyDetails(
                {
                    assesseeOrgId: params.id,
                    investorOrgId: user.domainMetadata?.investorOrgId,
                },
                {
                    onSuccess: (companyData) => {
                        callbackFunction?.();
                        setSelectedCompany(companyData);
                    },
                },
            ),
        );
        dispatch(
            fetchAicaGradeData({
                assesseeOrgId: params.id as string,
                investorOrgId: user.domainMetadata?.investorOrgId as string,
            }),
        );
    };

    const shouldRenderSecondA4Page = () => {
        const hasStatCardData = Object.keys(headlinePanelData || {}).some(
            (statName) => statName !== 'legalCases' && headlinePanelData?.[statName]?.value,
        );

        // Check if any Graph has data
        const hasGraphData = chartsToShow.some((chartName) => {
            const chartData = chartsData[chartName];
            return chartData?.isFetched ? chartData?.plots?.[0]?.plotValues?.length || chartData?.plots?.[0]?.yearMonthMap : true;
        });
        return hasStatCardData || hasGraphData;
    };

    const renderHeaderButton = () => {
        if (isUserRestricted === false) {
            if (isScanLockedForUser) return <UnlockScanButton onClick={() => dispatch(setShowUpgradeToScan(ACCESS_TIERS.SCAN))} />;
            else if (selectedCompany?.companyAccessType == ACCESS_TIERS.WATCH)
                return (
                    <AddToScanButton
                        onClick={() => {
                            dispatch(
                                setShowUnlockAicaAndAddToLeads({
                                    source: UnlockAICASourcePage.DASHBOARD_HEADER,
                                    showModal: true,
                                }),
                            );
                        }}
                        accessType={selectedCompany?.companyAccessType}
                        isDemoProfile={isDemoProfile}
                    />
                );
            else if (user.userData.accountType === USER_TYPE.INVESTOR && !isDemoProfile && showDataGapCTA) {
                return (
                    <>
                        <div
                            className={styles.DataRequestCTA}
                            onClick={() => {
                                searchParams.set(SOURCE.BORROWER_REQ, 'true');
                                setSearchParams(searchParams);
                            }}
                        >
                            <img src={needInfoSelected} alt="Data Requests" />
                            <span>Data Requests</span>
                        </div>
                    </>
                );
            }
        }
    };

    const renderAicaOverview = () => {
        if (['compliances', 'creditBureau', 'auditedFinancials', 'bankingAndFinancials'].includes(dashboardTab.selectedTab)) {
            return <AicaOverview style={{ marginTop: '1rem' }} module={dashboardTab.selectedTab} isPDFReport={!!pdfReportSection} />;
        }
    };

    const renderDynamicTabContent = (isPDFReport = false) => (
        <>
            {!isPDFReport && renderAicaOverview()}
            {dataType === DASHBOARD_DATA_TYPES.CHARTS ? (
                <>
                    {
                        <>
                            <div
                                className={clsx(
                                    {
                                        [styles.CompliancesStats]: dashboardTab.selectedTab === HEADLINE_PANEL_CONSTANTS.COMPLIANCES,
                                        [styles.StatsPDFReport]: isPDFReport,
                                    },
                                    styles.Stats,
                                )}
                            >
                                {dashboardDataNew.loadingHeadline
                                    ? Array.from({
                                          length: dashboardTab.selectedTab === HEADLINE_PANEL_CONSTANTS.COMPLIANCES ? 3 : 4,
                                      }).map((_, index) => (
                                          <StatCard
                                              data={{}}
                                              key={`placeholder-${index}`}
                                              statName={`placeholder-${index}`}
                                              isLoading={true}
                                              isPDFReport={!!pdfReportSection}
                                          />
                                      ))
                                    : headlinePanelData &&
                                      dashboardTab.selectedTab !== 'bankingAndFinancials' &&
                                      Object.keys(headlinePanelData || {})
                                          ?.filter(
                                              (statName) =>
                                                  headlinePanelData[statName]?.title !== HEADLINE_PANEL_CONSTANTS.OPEN_LEGAL_CASES,
                                          )
                                          .map((statName) => (
                                              <StatCard
                                                  data={headlinePanelData?.[statName]}
                                                  key={statName}
                                                  statName={statName}
                                                  isLoading={false}
                                                  isPDFReport={!!pdfReportSection}
                                              />
                                          ))}
                            </div>

                            <div
                                className={clsx(styles.GraphsContainer, {
                                    [styles.GraphsContainerPDFReport]: isPDFReport,
                                })}
                            >
                                {chartsToShow?.map((chartName, index) => (
                                    <div
                                        key={`${chartName}-${index}}`}
                                        style={{
                                            gridRow: getGridRowHeight(chartName, index),
                                        }}
                                    >
                                        {!!chartsData[chartName] &&
                                            (chartsData[chartName]?.isFetched && isPDFReport
                                                ? (chartsData[chartName]?.plots?.[0]?.plotValues || [])?.length ||
                                                  chartsData[chartName]?.plots?.[0]?.yearMonthMap
                                                : true) && (
                                                <Graph
                                                    key={`${chartName}-${index}`}
                                                    graphData={chartsData[chartName]}
                                                    graphName={chartName}
                                                    selectedPeriod={periodSelectorData.selectedPeriod}
                                                    fetchGraph={fetchGraph}
                                                    user={user}
                                                    showAlerts={showAlerts}
                                                    initialiseHeadlinePanelPolling={initialiseHeadlinePanelPolling}
                                                    isPDFReport={isPDFReport}
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>
                        </>
                    }
                </>
            ) : (
                <>{getTableData()}</>
            )}
        </>
    );

    // A registered mobile section is skipped when its optional `permission` key
    // resolves to LOCKED for this investor — mirrors the existing ACCESS_TIERS gating.
    const sectionPermitted = (section: MobileCompanySection) =>
        !section.permission || permissions?.[section.permission] !== ACCESS_TYPES.LOCKED;

    const render = (
        <div
            className={clsx(styles.Dashboard, {
                [styles.Animate]: !pdfReportSection,
                [styles.PDFReport]: isPDFReport,
            })}
        >
            <div className={styles.PageWrapper}>
                <div className={styles.Sidebar}>
                    <TabSelector
                        tabs={visibleTabs}
                        currentTab={dashboardTab.selectedTab}
                        setCurrentTab={(tab) => {
                            mixpanelRegisterTabChangeClick(tab?.id, selectedCompany);
                            if (tab?.id === TabModule.GST) {
                                Mixpanel.track(AICA_GST_MODULE_CLICKED, {
                                    parentId: user.userData.investorOrgId,
                                    websiteUrl: window.location.href,
                                    companyOrgId: params.id,
                                });
                            }
                            setDataType(tab?.id === 'bankingAndFinancials' ? DASHBOARD_DATA_TYPES.TABLE : DASHBOARD_DATA_TYPES.CHARTS);
                            setDashboardTab({ selectedTab: tab?.id, tabType: tab?.type });
                            let mutated = false;
                            // Clear dataType param when switching to tabs that don't use it
                            if (
                                ![TabModule.REVENUE, TabModule.VENDOR_EXPENSES, TabModule.GST].includes(tab?.id) &&
                                searchParams.has('dataType')
                            ) {
                                searchParams.delete('dataType');
                                mutated = true;
                            }
                            // `subTab` only makes sense inside the GST module — strip it when
                            // navigating to any other sidebar tab so the URL stays clean.
                            if (tab?.id !== TabModule.GST && searchParams.has('subTab')) {
                                searchParams.delete('subTab');
                                mutated = true;
                            }
                            if (mutated) setSearchParams(searchParams);
                        }}
                        companyAccessType={selectedCompany?.companyAccessType || ''}
                        unlockedAt={selectedCompany?.unlockAt || ''}
                        selectedCompany={selectedCompany}
                    />
                </div>
                <div className={styles.SidebarBorder}></div>
                <div
                    className={clsx(styles.MainContentWrapper, {
                        [styles.MainContentWrapperGstOverview]: dashboardTab.selectedTab === TabModule.GST,
                    })}
                >
                    {dashboardTab.selectedTab === TabModule.BANKING_AND_FINANCIALS &&
                        bankingExtractAllowed &&
                        ((bankingBannerMode === BANNER_MODE.IN_PROGRESS && hasInFlightBanking && bankingTotalCount > 0) ||
                            (bankingBannerMode === BANNER_MODE.PENDING && bankingPendingCount > 0 && !bankingBannerDismissed)) && (
                            <div className={styles.BankingBannerWrapper}>
                                <PendingBankStatementsBanner
                                    mode={bankingBannerMode}
                                    canExtract={bankingExtractAllowed}
                                    pendingCount={bankingPendingCount}
                                    inFlightCount={bankingInFlightCount}
                                    processedCount={bankingProcessedCount}
                                    totalCount={bankingTotalCount}
                                    onExtractClick={() => extractBankStatements(CLICK_SOURCE.BANNER)}
                                    onDismiss={() => dispatch(dismissBanner({ orgId: params.id as string }))}
                                />
                            </div>
                        )}
                    <div className={styles.MainContent}>
                        {dashboardTab.selectedTab === TabModule.GST ? (
                            <GstOverview isPDFReport={isPDFReport} pdfReportSection={pdfReportSection} />
                        ) : dashboardTab.selectedTab === TabModule.REVENUE || dashboardTab.selectedTab === TabModule.VENDOR_EXPENSES ? (
                            // Legacy sidebar entries route through GstAnalysisPanel for
                            // data parity with the GST module sub-tabs. Branch is dead
                            // once godzilla hides the legacy tabs + URL redirect re-enables.
                            <GstAnalysisPanel
                                tab={dashboardTab.selectedTab as typeof TabModule.REVENUE | typeof TabModule.VENDOR_EXPENSES}
                                isPDFReport={isPDFReport}
                                pdfReportSection={pdfReportSection}
                            />
                        ) : (
                            <>
                                <div
                                    className={clsx(styles.PeriodSelectorRow, {
                                        [styles.PeriodSelectorRowWithBanner]: isBankingBannerVisible,
                                    })}
                                    style={{
                                        borderBottom: dashboardTab.tabType != 'static' ? '1px solid var(--border-color-new)' : 'none',
                                        display: dashboardTab.tabType === 'static' ? 'none' : undefined,
                                    }}
                                >
                                    <div className={styles.PeriodSelectorRowLeft}>
                                        {dashboardTab.tabType != 'static' && (
                                            <div className={styles.TabTitle}>
                                                {dashboardConfig?.tabs?.find((tab) => tab.id === dashboardTab.selectedTab)?.label ||
                                                    dashboardTab.selectedTab}
                                            </div>
                                        )}
                                    </div>
                                    {dashboardTab.tabType === 'dynamic' && dashboardTab.selectedTab !== 'bankingAndFinancials' && (
                                        <div className={styles.LatestUpdatedContainer}>
                                            {dashboardTab.selectedTab === TabModule.COMPLIANCES && (
                                                <McaRefreshLoader mcaDocData={mcaDocData} />
                                            )}
                                            {['compliances'].includes(dashboardTab.selectedTab) && (
                                                <div className={styles.LatestUpdated}>
                                                    <span>Last Updated on {latestFetchDate}</span>
                                                </div>
                                            )}
                                            <div>
                                                <DataSource
                                                    dataSource={dataSource}
                                                    isLoading={dashboardDataNew.loadingHeadline}
                                                    selectedTab={dashboardTab.selectedTab}
                                                    isPDFReport={!!pdfReportSection}
                                                    orgId={params.id}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {dashboardTab.tabType === 'static' && selectedCompany && <>{getStaticComponent()}</>}
                                {dashboardTab.tabType === 'dynamic' && <>{renderDynamicTabContent()}</>}
                            </>
                        )}
                    </div>
                    {!isPDFReport && <AskAicaV2 selectedCompany={selectedCompany} global />}
                </div>

                {showUpgradeToScanModal && (
                    <UnlockScanModal
                        showUpgradeToScanModal={showUpgradeToScanModal}
                        openModal={showUpgradeToScanModal}
                        closeModal={() => {
                            dispatch(setShowUpgradeToScan(''));
                        }}
                    />
                )}

                {showInviteCompanyModal && (
                    <InviteCompanyModal
                        screen={INVITE_SCREENS.DASHBOARD}
                        modalState={showInviteCompanyModal}
                        closeModal={() => setShowInviteCompanyModal(false)}
                        assesseeOrgId={params.id}
                        investorOrgId={user.userData.investorOrgId}
                        platformId={user.domainMetadata?.productName}
                        selectedCompany={selectedCompany}
                        companyAccessType={selectedCompany?.companyAccessType}
                        openInviteUserModal={() => dispatch(setShowInviteUserModal(true))}
                    />
                )}

                {showDealDrawer && <ManageDealDrawer source={source === 'Debt Products' ? 'Debt Products' : 'AICA Report Page'} />}

                {showFinancialMetrics && (
                    <FinancialMetrics
                        screen="dashboard"
                        assesseeOrgId={params.id}
                        onClose={() => {
                            deleteFromSearchParams('showFinancialMetrics');
                        }}
                    />
                )}

                {unlockAicaReportDetails?.showUnlockAicaConfirmation && (
                    <ConfirmationPopup
                        title="Unlock AICA?"
                        subTitle="To add to portfolio and start monitoring you need to unlock AICA Report."
                        confirmCta="Unlock"
                        cancelCta="Cancel"
                        open={unlockAicaReportDetails?.showUnlockAicaConfirmation}
                        onConfirm={() => {
                            dispatch(setShowUnlockAicaConfirmation(false));
                            dispatch(setShowRefreshReport(true));
                        }}
                        onCancel={() => {
                            dispatch(setShowUnlockAicaConfirmation(false));
                        }}
                    />
                )}

                {showPortfolioSuccess && <PortfolioSuccess isOpen={showPortfolioSuccess} />}

                {unlockAicaAndAddToLeadsConfirmation.showConfirmationModal && (
                    <ConfirmationPopup
                        title={
                            unlockAicaAndAddToLeadsConfirmation.source === UnlockAICASourcePage.DASHBOARD_SIDEBAR
                                ? 'Unlock AICA?'
                                : 'Add to Leads?'
                        }
                        subTitle={
                            unlockAicaAndAddToLeadsConfirmation.source === UnlockAICASourcePage.DASHBOARD_SIDEBAR
                                ? 'To unlock AICA Report you need to add it to leads.'
                                : ''
                        }
                        confirmCta={
                            unlockAicaAndAddToLeadsConfirmation.source === UnlockAICASourcePage.DASHBOARD_SIDEBAR
                                ? 'Unlock and Add to Leads'
                                : 'Add to Leads'
                        }
                        cancelCta="Cancel"
                        open={unlockAicaAndAddToLeadsConfirmation.showConfirmationModal}
                        onConfirm={(onStart, onSuccess) => {
                            onStart?.();
                            moveTierAndUnlockAICAReport(params.id ?? '', ACCESS_TIERS.SCAN, user.userData.investorOrgId, () => {
                                onSuccess?.();
                                dispatch(
                                    setShowUnlockAicaAndAddToLeads({
                                        showModal: false,
                                    }),
                                );
                                onUnlockReportSuccess();
                                setShowInviteCompanyModal(true);
                            });
                        }}
                        onCancel={() => {
                            dispatch(
                                setShowUnlockAicaAndAddToLeads({
                                    showModal: false,
                                }),
                            );
                        }}
                    />
                )}
            </div>
        </div>
    );

    useEffect(() => {
        if (!isMobile || !selectedCompany?.assesseeOrgId) return;
        const fromPath = investorData.mobileOverviewSource;
        Mixpanel.track(INVESTOR_MOBILE_COMPANY_VIEW, {
            assesseeOrgId: selectedCompany?.assesseeOrgId,
            companyName: selectedCompany?.orgName,
            source: getMobileOverviewSource(fromPath),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- orgName is a read-at-fire-time enrichment prop; excluding it keeps this to one view event per company (adding it would double-fire when orgName resolves after the id)
    }, [isMobile, selectedCompany?.assesseeOrgId]);

    if (pdfReportSection)
        return reportSectionsToShow.includes(REPORT_SECTION_NAMES.COMPLIANCES_SUMMARY) && showPDFReportSection ? (
            <>
                <A4Page sectionName={REPORT_SECTION_NAMES.COMPLIANCES_SUMMARY_OVERVIEW}>
                    <AicaOverview style={{ marginTop: '1.5rem' }} module={'compliances'} isPDFReport={!!pdfReportSection} />
                </A4Page>
                {shouldRenderSecondA4Page() && (
                    <A4Page sectionName={REPORT_SECTION_NAMES.COMPLIANCES_SUMMARY}>
                        <div className={styles.Title}>
                            Compliances Summary
                            <DataSource
                                dataSource={dataSource}
                                isLoading={dashboardDataNew.loadingHeadline}
                                selectedTab={dashboardTab.selectedTab}
                            />
                        </div>

                        {renderDynamicTabContent(!!pdfReportSection)}
                    </A4Page>
                )}
            </>
        ) : (
            <></>
        );

    if (isMobile) {
        return (
            // data-mobile-shell keeps the Overview card's SCSS (card-mobile/card-medium
            // mixins) in lockstep with this useIsMobile branch — see _cardLayout.scss
            <div className={styles.MobileCompanyDetail} data-mobile-shell="">
                <DashboardHeader
                    isMobile
                    selectedCompany={selectedCompany}
                    isUserRestricted={isUserRestricted}
                    refreshReportStatus={refreshReportStatus}
                    downloadPDFStatus={downloadPDFStatus}
                    goBackToPortfolio={() => {
                        const from = investorData.mobileOverviewSource;
                        dispatch(setMobileOverviewSource(null));
                        Mixpanel.track(INVESTOR_MOBILE_BACK_TO_LIST, {
                            destination: from || INVESTOR_ROUTES.APP_SCAN,
                            source: getMobileOverviewSource(from),
                            assesseeOrgId: selectedCompany?.assesseeOrgId,
                        });
                        navigate(from || INVESTOR_ROUTES.APP_SCAN);
                    }}
                    renderHeaderButton={() => null}
                    exportData={exportData}
                />
                <div className={styles.MobileSections}>
                    {mobileCompanySections.filter(sectionPermitted).map((section) => (
                        <Fragment key={section.key}>
                            {section.render({ selectedCompany, refetchOverviewRef, appendSearchParams, navigateToTab, triggerMain })}
                        </Fragment>
                    ))}
                </div>
                <MobileAskAicaOrb onClick={() => setMobileAskAicaOpen(true)} />
                {mobileAskAicaOpen && (
                    <Suspense fallback={null}>
                        <MobileAskAicaScreen
                            selectedCompany={selectedCompany as MobileAskAicaCompany}
                            onClose={() => setMobileAskAicaOpen(false)}
                        />
                    </Suspense>
                )}
            </div>
        );
    }

    return render;
}

export default Dashboard;
